import React, { useMemo } from "react";
import SearchBox from "library/components/_tailwind/search-box";
import { inject, observer } from "mobx-react";
import TailwindBox from "library/components/_tailwind/box";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import TailwindList, {
  TailwindListType,
} from "library/components/_tailwind/list/";
import TailwindListItem from "library/components/_tailwind/list/list-item";
import TailwindIcon from "library/components/_tailwind/icon";
import CopyToClipboard from "library/core/utility/copy-to-clipboard";
import SnackbarStore from "library/core/stores/snackbar/SnackbarStore";
import { SnackbarVariants } from "library/core/stores/snackbar/enums";
import BroadcastStreamStore from "common/broadcast/_stores/broadcast-stream/BroadcastStreamStore";
import LanguageStore from "library/core/stores/language/LanguageStore";

interface Props {
  broadcastStreamStore?: BroadcastStreamStore;
  languageStore?: LanguageStore;
  snackbarStore?: SnackbarStore;
}

const BroadcastVRSteps: React.FunctionComponent<Props> = ({
  broadcastStreamStore,
  languageStore,
  snackbarStore,
}) => {
  const {
    vrBroadcastUrl,
    obsBroadcastKey,
    hasPublishToken,
    isFetchingPublishToken,
    getPublishToken,
  } = broadcastStreamStore!;
  const { enqueueSnackbar } = snackbarStore!;

  const { intl } = languageStore!;

  const copyToClipboard = new CopyToClipboard();

  const broadcastUrlValue = useMemo(() => {
    return isFetchingPublishToken
      ? intl.formatMessage({
          id: "broadcast-obs-modal.loading-publish-token",
          defaultMessage: "Loading publish token",
        })
      : hasPublishToken
      ? vrBroadcastUrl
      : intl.formatMessage({
          id: "broadcast-obs-modal.publish-token-not-fetched",
          defaultMessage: "Publish token not fetched",
        });
  }, [intl, isFetchingPublishToken, hasPublishToken, vrBroadcastUrl]);

  return (
    <TailwindBox fontSize={"text-sm"}>
      <TailwindTranslatedText
        fontWeight={"font-bold"}
        textColor='text-main-color'
        fontSize={"text-sm"}
        descriptor={{
          id: "broadcast-vr-modal.launch-vr",
          defaultMessage:
            "If you have done these steps before, you may ignore the steps and start streaming in your Camera Companion App.",
        }}
        margin={["mb-4"]}
      />
      <br />
      <br />
      <TailwindTranslatedText
        textColor='text-main-color'
        fontSize={"text-sm"}
        descriptor={{
          id: "broadcast-vr-modal.connect-vr-camera",
          defaultMessage: "Connect your VR camera and follow these steps:",
        }}
      />
      <TailwindList type={TailwindListType.ol} margin={["ml-3"]}>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-1",
              defaultMessage:
                "Make sure your VR camera is connected to your computer",
            }}
          />
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-2",
              defaultMessage: "Open VR companion app on your desktop",
            }}
          />
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-3",
              defaultMessage: `Choose "External Connection" in settings`,
            }}
          />
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-4",
              defaultMessage: `Copy and paste this into the "SERVER URL" field:`,
            }}
          />
          &nbsp;
          <TailwindBox
            backgroundColor={"bg-white"}
            borderRadius={"rounded-md"}
            width={"w-full"}
            height={"h-8"}
            display={"inline-flex"}>
            <SearchBox
              containerProps={{ inline: true }}
              inputProps={{
                "data-testid": "obs-broadcast-url",
                readOnly: true,
                defaultValue: broadcastUrlValue,
                value: broadcastUrlValue,
                id: vrBroadcastUrl,
                backgroundColor: "bg-transparent",
                flexProps: {
                  inline: true,
                },
              }}
              buttonProps={
                hasPublishToken
                  ? {
                      children: <TailwindIcon name='file_copy' />,
                      onClick: () => {
                        copyToClipboard.copy(vrBroadcastUrl);
                        enqueueSnackbar({
                          message: {
                            id: "admirers.referrals.myReferralLink.memberLinkCopy",
                            default: "LINK COPIED!",
                          },
                          closeButton: true,
                          variant: SnackbarVariants.SUCCESS,
                        });
                      },
                    }
                  : {
                      children: <TailwindIcon name='refresh' />,
                      onClick: () => {
                        if (!isFetchingPublishToken) {
                          getPublishToken();
                        }
                      },
                    }
              }
            />
          </TailwindBox>
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-5",
              defaultMessage:
                'Copy and paste this into the "SEISSION KEY" field:',
            }}
          />
          &nbsp;
          <TailwindBox
            backgroundColor={"bg-white"}
            borderRadius={"rounded-md"}
            width={"w-full"}
            height={"h-8"}
            display={"inline-flex"}>
            <SearchBox
              containerProps={{ inline: true }}
              inputProps={{
                "data-testid": "obs-broadcast-key",
                readOnly: true,
                defaultValue: obsBroadcastKey,
                value: obsBroadcastKey,
                id: obsBroadcastKey,
                backgroundColor: "bg-transparent",
                flexProps: {
                  inline: true,
                },
              }}
              buttonProps={{
                children: <TailwindIcon name='file_copy' />,
                onClick: () => {
                  copyToClipboard.copy(obsBroadcastKey);
                  enqueueSnackbar({
                    message: {
                      id: "admirers.referrals.myReferralLink.memberLinkCopy",
                      default: "LINK COPIED!",
                    },
                    closeButton: true,
                    variant: SnackbarVariants.SUCCESS,
                  });
                },
              }}
            />
          </TailwindBox>
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-6",
              defaultMessage:
                "Select your preferred VR quality (High, Medium, Low)",
            }}
          />
        </TailwindListItem>
        <TailwindListItem>
          <TailwindTranslatedText
            fontSize={"text-sm"}
            descriptor={{
              id: "broadcast-vr-modal.step-7",
              defaultMessage: `Click "Connect" to establish the connection`,
            }}
          />
        </TailwindListItem>
      </TailwindList>
    </TailwindBox>
  );
};

export default inject(
  "broadcastStreamStore",
  "languageStore",
  "snackbarStore"
)(observer(BroadcastVRSteps));
